/**
 * Zustand store pro BMS state.
 *
 *  - `devices`: seznam z DB + runtime stav (status, deviceInfo, settings)
 *  - `telemetry`: poslední 1 Hz snapshot per slug (živý stream)
 *  - `cellHistory`: posledních N (300) snapshotů per slug pro mini-grafy živého streamu
 *
 * Aktualizováno z WS handlerů v ws-client.ts.
 */

import { create } from "zustand";
import type { BmsListItem, BmsOverview, BmsRuntime, BmsStateWsEvent, BmsTelemetry } from "@client/types/bms";

const MAX_LIVE_HISTORY = 300;

interface BmsStore {
  devices: BmsListItem[];
  /** Plná telemetrie per slug — jen pokud je socket subscribed na full pro tento slug. */
  telemetry: Map<string, BmsTelemetry>;
  /** Lite snapshot per slug — pro DevicesPage karty (subscribed přes overview flag). */
  overview: Map<string, BmsOverview>;
  /** Posledních N (300) telemetry snapshotů per slug pro cell-history grafy. */
  cellHistory: Map<string, BmsTelemetry[]>;
  loaded: boolean;

  setDevices: (devices: BmsListItem[]) => void;
  /** Plný snapshot z `bms:telemetry:full` — replace + append do historie. */
  applyTelemetryFull: (slug: string, t: BmsTelemetry) => void;
  /** Shallow merge patch z `bms:telemetry:diff` — vytvoří novou referenci. */
  applyTelemetryDiff: (slug: string, patch: Partial<BmsTelemetry>) => void;
  applyOverviewFull: (slug: string, o: BmsOverview) => void;
  applyOverviewDiff: (slug: string, patch: Partial<BmsOverview>) => void;
  applyState: (ev: BmsStateWsEvent) => void;
  clear: () => void;
}

export const useBmsStore = create<BmsStore>((set) => ({
  devices: [],
  telemetry: new Map(),
  overview: new Map(),
  cellHistory: new Map(),
  loaded: false,

  setDevices: (devices) => set({ devices, loaded: true }),

  applyTelemetryFull: (slug, t) =>
    set((state) => {
      const newTelemetry = new Map(state.telemetry);
      newTelemetry.set(slug, t);
      const newHistory = new Map(state.cellHistory);
      const prev = newHistory.get(slug) ?? [];
      newHistory.set(slug, [...prev, t].slice(-MAX_LIVE_HISTORY));
      return { telemetry: newTelemetry, cellHistory: newHistory };
    }),

  applyTelemetryDiff: (slug, patch) =>
    set((state) => {
      const prev = state.telemetry.get(slug);
      if (!prev) return state; // diff bez full → ignorovat (přijde resync)
      const merged: BmsTelemetry = { ...prev, ...patch };
      const newTelemetry = new Map(state.telemetry);
      newTelemetry.set(slug, merged);
      // Do historie přidáváme merged snapshot — sparklines / cell history zachovají
      // kontinuitu (každý tick = jeden záznam, jen rozdílná pole jsou updatovaná).
      const newHistory = new Map(state.cellHistory);
      const prevHist = newHistory.get(slug) ?? [];
      newHistory.set(slug, [...prevHist, merged].slice(-MAX_LIVE_HISTORY));
      return { telemetry: newTelemetry, cellHistory: newHistory };
    }),

  applyOverviewFull: (slug, o) =>
    set((state) => {
      const newOverview = new Map(state.overview);
      newOverview.set(slug, o);
      return { overview: newOverview };
    }),

  applyOverviewDiff: (slug, patch) =>
    set((state) => {
      const prev = state.overview.get(slug);
      if (!prev) return state;
      const newOverview = new Map(state.overview);
      newOverview.set(slug, { ...prev, ...patch });
      return { overview: newOverview };
    }),

  applyState: (ev) =>
    set((state) => {
      const devices = state.devices.map((d) => {
        if (d.slug !== ev.slug) return d;
        const runtime: BmsRuntime = d.runtime ?? {
          slug: d.slug,
          status: "idle",
          deviceInfo: null,
          settings: null,
          telemetry: null,
          lastPublishedAt: 0,
          lastTelemetryAt: 0,
        };
        return {
          ...d,
          runtime: {
            ...runtime,
            status: ev.status ?? runtime.status,
            deviceInfo: ev.deviceInfo ?? runtime.deviceInfo,
            settings: ev.settings ?? runtime.settings,
          },
        };
      });
      return { devices };
    }),

  clear: () =>
    set({ devices: [], telemetry: new Map(), overview: new Map(), cellHistory: new Map(), loaded: false }),
}));
